import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';

import {
  useGetCompaniesByCompanyIdContactPersonAssignmentsQuery,
  useGetTenantsCurrentCompanyQuery,
} from '@client/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Text,
} from '@client/toolkit';

import { PageContentAnimation } from '../../../components/layout';
import { AgreementsPaths } from '../../agreements/agreements.router';
import { useTenants } from '../../../hooks/useTenants/useTenants';
import { TenantSelectionEnum } from '../../../redux';

export const ProfileContactPersons = () => {
  const { t } = useTranslation('companies');
  const { t: tType } = useTranslation('type');

  const { selectedTenant } = useTenants();
  const tenant = selectedTenant && selectedTenant !== TenantSelectionEnum.All ? selectedTenant : null;

  const { data: company } = useGetTenantsCurrentCompanyQuery(undefined, {
    skip: !tenant,
  });

  const companyId = company?.id;

  const { data: assignments, isFetching, isLoading } = useGetCompaniesByCompanyIdContactPersonAssignmentsQuery(
    { companyId: companyId ?? '' },
    { skip: !companyId },
  );

  const isEmpty = !isLoading && (!assignments || assignments.length === 0);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle variant="highlighted">{t('detailTabs.contactPersons')}</CardTitle>
        </CardHeader>
        <CardContent variant="fill" className="rounded-lg w-full px-4 pb-4">
          <Table className="overflow-hidden w-full mb-4 rounded-md" size="xs" layout="fixed">
            {isEmpty && (
              <caption className="px-4 py-4 [caption-side:bottom]">
                <EmptyPlaceholder>{t('contactPersons.noContactPersons')}</EmptyPlaceholder>
              </caption>
            )}

            <TableHead>
              <TableHeadRow>
                <TableHeadCell className="w-[20%]">{t('contactPersons.contactPerson')}</TableHeadCell>
                <TableHeadCell className="w-[15%]">{t('contactPersons.role')}</TableHeadCell>
                <TableHeadCell className="w-[25%]">{t('contactPersons.agreement')}</TableHeadCell>
                <TableHeadCell className="w-[20%]">{t('contactPersons.email')}</TableHeadCell>
                <TableHeadCell className="w-[20%]">{t('contactPersons.phone')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>

            <TableBody>
              {isLoading &&
                Array.from(Array(3).keys()).map((x) => (
                  <TableRow key={x} verticalAlign="middle">
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                  </TableRow>
                ))}

              {!isLoading &&
                assignments?.map((assignment, index) => (
                  <TableRow key={`${assignment.agreementId}-${assignment.role}-${index}`} verticalAlign="middle">
                    <TableCell loading={isFetching}>
                      <Text as="div" isEmpty={!assignment.contactPerson?.name}>
                        {assignment.contactPerson?.name ?? '—'}
                      </Text>
                    </TableCell>
                    <TableCell loading={isFetching}>
                      <Text as="div">
                        {tType(`RelationshipType.${assignment.role}`)}
                      </Text>
                    </TableCell>
                    <TableCell loading={isFetching}>
                      <Link
                        to={generatePath(AgreementsPaths.AgreementParties, { id: assignment.agreementId })}
                        className="text-brand-primary hover:underline whitespace-nowrap"
                      >
                        {assignment.agreementName}
                      </Link>
                    </TableCell>
                    <TableCell loading={isFetching}>
                      <Text as="div" isEmpty={!assignment.contactPerson?.email}>
                        {assignment.contactPerson?.email ?? '—'}
                      </Text>
                    </TableCell>
                    <TableCell loading={isFetching}>
                      <Text as="div" isEmpty={!assignment.contactPerson?.phone}>
                        {assignment.contactPerson?.phone ?? '—'}
                      </Text>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
};
